import React from 'react';
import { Tag, Tooltip } from 'antd';
import {
  PlayCircleOutlined,
  RobotOutlined,
  BranchesOutlined,
  UserOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  TagsOutlined,
  DatabaseOutlined,
  ApiOutlined,
  CalculatorOutlined,
  UserSwitchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { FlowNode, NODE_STYLES, VisualNodeType, CARD_WIDTH, CARD_HEIGHT } from '../types/flow';

const ICON_MAP: Record<VisualNodeType, React.ReactNode> = {
  start: <PlayCircleOutlined />,
  llm: <RobotOutlined />,
  condition: <BranchesOutlined />,
  human_input: <UserOutlined />,
  extraction: <FileSearchOutlined />,
  classification: <TagsOutlined />,
  bigquery_save: <DatabaseOutlined />,
  lead_enrichment: <UserSwitchOutlined />,
  estimate_budget: <CalculatorOutlined />,
  http_request: <ApiOutlined />,
};

interface FlowNodeCardProps {
  node: FlowNode;
  x: number;
  y: number;
  selected: boolean;
  onClick: (node: FlowNode) => void;
  onDelete?: (nodeId: string) => void;
}

const FlowNodeCard: React.FC<FlowNodeCardProps> = ({
  node,
  x,
  y,
  selected,
  onClick,
  onDelete,
}) => {
  const isHub = node.type === 'start';
  const style = NODE_STYLES[node.type];

  if (isHub) {
    return (
      <div
        className="flow-node-card"
        style={{
          position: 'absolute',
          left: x,
          top: y,
          width: CARD_WIDTH,
          height: CARD_HEIGHT,
          background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
          borderRadius: 14,
          border: `2px solid ${selected ? '#fff' : 'rgba(255,255,255,0.5)'}`,
          boxShadow: selected
            ? '0 0 0 3px rgba(82, 196, 26, 0.25), 0 6px 18px rgba(82, 196, 26, 0.3)'
            : '0 4px 14px rgba(82, 196, 26, 0.25)',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '12px 16px',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          userSelect: 'none',
          zIndex: 3,
          color: '#fff',
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClick(node);
        }}
      >
        <div style={{ fontSize: 22, lineHeight: 1, marginBottom: 4 }}>
          <ThunderboltOutlined />
        </div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>
          {node.label || 'Inicio'}
        </div>
        <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
          Pipeline
        </div>
      </div>
    );
  }

  return (
    <div
      className="flow-node-card"
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        background: '#ffffff',
        borderRadius: 12,
        border: `2px solid ${selected ? style.color : '#e8e8e8'}`,
        boxShadow: selected
          ? `0 0 0 2px ${style.color}33, 0 4px 12px rgba(0,0,0,0.08)`
          : '0 2px 8px rgba(0,0,0,0.06)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '12px 16px',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        userSelect: 'none',
        zIndex: 2,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick(node);
      }}
    >
      {/* Delete button */}
      {onDelete && (
        <Tooltip title="Eliminar herramienta">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(node.id);
            }}
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 22,
              height: 22,
              borderRadius: '50%',
              border: '1px solid #ff4d4f',
              background: '#fff',
              color: '#ff4d4f',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              padding: 0,
              opacity: 0,
              transition: 'opacity 0.2s',
              zIndex: 3,
            }}
            className="node-delete-btn"
          >
            <DeleteOutlined />
          </button>
        </Tooltip>
      )}

      <div style={{ marginBottom: 4 }}>
        <Tag
          style={{
            background: style.bgColor,
            color: style.color,
            border: `1px solid ${style.borderColor}`,
            fontSize: 11,
            lineHeight: '18px',
            padding: '0 6px',
            borderRadius: 4,
          }}
        >
          {ICON_MAP[node.type]}{' '}
          <span style={{ marginLeft: 4 }}>{style.tagLabel}</span>
        </Tag>
      </div>

      <div
        style={{
          fontWeight: 600,
          fontSize: 14,
          color: '#1a1a2e',
          lineHeight: '20px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {node.label}
      </div>

      {node.subtitle && (
        <div
          style={{
            fontSize: 12,
            color: '#8c8c8c',
            lineHeight: '16px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {node.subtitle}
        </div>
      )}
    </div>
  );
};

export default FlowNodeCard;
